import Image from 'next/image'
import Link from 'next/link'
import { trackMxEvent } from '@/utils/mixpanel'
import { SERVICE_LIST } from '@/data/service'
import useAuth from '@/hooks/useAuth'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { SOUND_REVIEW_SLUG } from '@/config/sound-review'

type ListItemProps = {
  icon?: string
  title: string
  href: string
  children: React.ReactNode
  isHowto?: boolean
  eventName?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventProps?: Record<string, any>
  className?: string
  badge?: string
}
const ListItem = ({
  icon,
  title,
  href,
  children,
  isHowto = false,
  eventName = 'Header Pro Category Click',
  eventProps = undefined,
  className,
  badge,
}: ListItemProps) => {
  const mxEventProps = eventProps ?? (isHowto
    ? {}
    : {
      category_name: title,
      category_slug: href.replace('/cat/', ''),
    })

  return (
    <li className={className}>
      <NavigationMenuLink asChild>
        <Link
          className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block space-y-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none"
          href={href}
          onClick={() => trackMxEvent(eventName, mxEventProps)}
        >
          <div className="flex items-center text-sm leading-none font-medium">
            {icon && (
              <Image
                className="mr-2 h-4 w-4"
                src={`/images/home/${icon}.svg`}
                width="20"
                height="20"
                alt={`${title} icon`}
              />
            )}
            <span>{title}</span>
            {badge && (
              <span className="ml-2 rounded bg-[#FF8400] px-1 text-xs leading-5 font-medium text-white">
                {badge}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm leading-snug mb-0">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  )
}

export default function HeaderMenu() {
  const { currentUser } = useAuth()
  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger>サービス</NavigationMenuTrigger>
          <NavigationMenuContent className="w-[50rem]">
            <ul className="grid w-[50rem] grid-cols-[226px_1fr_1fr] gap-3 p-6">
              <li className="row-span-5">
                <NavigationMenuLink asChild>
                  <Link
                    href={`/service/${SERVICE_LIST[0].slug}`}
                    className="relative block rounded-md outline-hidden"
                    onClick={() => trackMxEvent('Header Service Click')}
                  >
                    <Image
                      className="h-[332px] w-[226px] rounded-lg object-cover"
                      src="/images/home/v3/nav-service.png"
                      width="226"
                      height="332"
                      alt="search pro background"
                    />
                  </Link>
                </NavigationMenuLink>
              </li>
              <ListItem
                href="/service/consulting/sound-review"
                title="サウンドレビュー"
                icon="/v3/nav/sound-review"
                className="col-span-2"
                badge="NEW"
                eventName="Header Service Category Click"
                eventProps={{
                  service_category_name: 'サウンドレビュー',
                  service_category_slug: SOUND_REVIEW_SLUG,
                }}
              >
                経験豊富なプロのクリエイターが、あなたの楽曲を聴いて本音でアドバイス
              </ListItem>
              {SERVICE_LIST.map((s) => (
                <ListItem
                  key={s.slug}
                  href={`/service/${s.slug}`}
                  title={s.service.replace('\n', '')}
                  icon={`/v3/nav/${s.image.replace('.png', '')}`}
                  eventName="Header Service Category Click"
                  eventProps={{
                    service_category_name: s.service.replace('\n', ''),
                    service_category_slug: s.slug,
                  }}
                >
                  {s.description}
                </ListItem>
              ))}
              <li className="col-span-2">
                <NavigationMenuLink asChild>
                  <Link
                    href={`/service/${SERVICE_LIST[0].slug}`}
                    className="border-border hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex items-center justify-center rounded-md border p-3 transition-colors hover:border-transparent"
                    onClick={() => trackMxEvent('Header Service Click')}
                  >
                    <Image
                      className="mr-2 h-4 w-4"
                      src="/images/home/v2/nav-cat.svg"
                      width="20"
                      height="20"
                      alt="category icon"
                    />
                    <span className="text-sm leading-none font-medium">
                      サービス一覧→
                    </span>
                  </Link>
                </NavigationMenuLink>
              </li>
              {/* <ListItem href="/search" title="カテゴリー" icon="cat">
                カテゴリーからクリエイターを検索
              </ListItem> */}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuTrigger>
            クリエイターを探す
          </NavigationMenuTrigger>
          <NavigationMenuContent className="w-md">
            <ul className="grid w-md grid-cols-[226px_1fr_1fr] gap-3 p-6">
              <li className="row-span-4">
                <NavigationMenuLink asChild>
                  <Link
                    href="/search"
                    className="relative block rounded-md outline-hidden"
                    onClick={() => trackMxEvent('Header Pro Search Click')}
                  >
                    <Image
                      className="h-[332px] w-[226px] rounded-lg object-cover"
                      src="/images/home/v2/nav-pro.png"
                      width="226"
                      height="332"
                      alt="search pro background"
                    />
                    <span className="h5 absolute inset-x-0 top-10 block text-center text-base font-medium text-white">
                      クリエイターを探す
                    </span>
                  </Link>
                </NavigationMenuLink>
              </li>
              <ListItem
                href="/cat/mix-engineer"
                title="ミックスエンジニア"
                icon="/v2/nav-mix"
              >
                各楽器の調整や加工を行い、楽曲を立体的に仕上げ、あなたの楽曲をプロクオリティに仕上げます。
              </ListItem>
              <ListItem
                href="/cat/beat-maker"
                title="ビートメーカー"
                icon="/v2/nav-beat"
              >
                ラップで使用するビートや、EDMなどのトラックメイキングなど。あなたのパフォーマンスを引き立たせるトラックを制作します。
              </ListItem>
              <ListItem
                href="/cat/arranger"
                title="アレンジャー"
                icon="/v2/nav-arng"
              >
                あなたのデモをブラッシュアップ。プロならではの視点から、楽曲の魅力を最大限引き出します。
              </ListItem>
              <ListItem
                href="/cat/producer"
                title="プロデューサー"
                icon="/v2/nav-pro"
              >
                あなたのアイディアを実現するため、方向性の相談から完パケまでをサポート。
              </ListItem>
              <ListItem
                href="/cat/song-writer"
                title="作曲家"
                icon="/v2/nav-comp"
              >
                メロディーメイクからトラック制作、あなたの要望に合った楽曲を制作します。
              </ListItem>
              <ListItem
                href="/cat/drummer"
                title="ドラマー"
                icon="/v2/nav-drum"
              >
                プロならではのドラムスキルで、あなたの楽曲にプロクオリティのドラムサウンドを提供します。
              </ListItem>
              <li className="col-span-2">
                <NavigationMenuLink asChild>
                  <Link
                    href="/search"
                    className="border-border hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex items-center justify-center rounded-md border p-3 transition-colors hover:border-transparent"
                  >
                    <Image
                      className="mr-2 h-4 w-4"
                      src="/images/home/v2/nav-cat.svg"
                      width="20"
                      height="20"
                      alt="category icon"
                    />
                    <span className="text-sm leading-none font-medium">
                      カテゴリーから探す→
                    </span>
                  </Link>
                </NavigationMenuLink>
              </li>
              {/* <ListItem href="/search" title="カテゴリー" icon="cat">
                カテゴリーからクリエイターを検索
              </ListItem> */}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <NavigationMenuTrigger>ONLIVE Studioについて</NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="flex w-140 gap-3 p-6">
              <Link
                href="/for/you"
                className="flex-none transition-all hover:hue-rotate-30"
                onClick={() => trackMxEvent('Header Howto Click')}
              >
                <div className="relative block h-[332px] w-[226px] flex-none rounded-md outline-hidden">
                  <Image
                    className="h-[332px] w-[226px] rounded-lg object-cover"
                    src="/images/home/v2/nav-for.png"
                    width="226"
                    height="332"
                    alt="abount background"
                  />
                  <span className="h5 absolute inset-x-4 top-10 block text-base font-medium text-white text-center">
                    ONLIVE Studio が<br />目指すもの
                  </span>
                </div>
              </Link>
              <ul className="space-y-3">
                <ListItem
                  href="/for/artist"
                  title="お仕事を依頼する"
                  icon="/v2/nav-ofr"
                  eventName="Header Howto User Click"
                  isHowto
                >
                  依頼したいクリエイターを検索して、プロジェクトを立ち上げて相談してみましょう。
                </ListItem>
                <ListItem
                  href="/for/pro"
                  title="依頼を受ける"
                  icon="/v2/nav-pro"
                  eventName="Header Howto Pro Click"
                  isHowto
                >
                  音楽制作に携わっている方、ミュージシャンなど、クリエイター登録していただける方を随時募集しています。
                </ListItem>
                <ListItem
                  href="/how-to-use"
                  title="活用シーン"
                  icon="/v2/nav-use"
                  eventName="Header Howto Pro Click"
                  isHowto
                >
                  音楽制作のさまざまなお悩みを解決する ONLIVE Studio。目的やシーンに合わせた活用方法をご案内します。
                </ListItem>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
        {currentUser?.isProfessional && (
          <>
            <div className="bg-border mx-2 h-5 w-px" />
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/competition/list"
                  className="hover:bg-accent hover:text-accent-foreground inline-flex h-9 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors"
                >
                  募集中のプロジェクト
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  )
}
